import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, ShieldAlert } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email, password, "admin");
      toast({ title: "Welcome, admin", description: "You're in the dispatch control room." });
      navigate("/admin-dashboard");
    } catch (err) {
      toast({
        title: "Sign-in failed",
        description: err?.message?.replace("Firebase: ", "") || "Please check your credentials.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="admin"
      title="Admin sign in"
      subtitle="Restricted access. All sessions are logged."
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0f2847]/5 border border-[#0f2847]/20 text-sm text-[#0f2847]">
          <ShieldAlert className="w-4 h-4 text-[#0f2847]" />
          Authorized personnel only.
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#0f2847] font-semibold">Admin email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@518courier.com"
              className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#0f2847] focus:ring-[#0f2847]"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#0f2847] font-semibold">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#0f2847] focus:ring-[#0f2847]"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#0f2847] font-semibold">2FA code (any 6 digits)</Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            maxLength={6}
            inputMode="numeric"
            className="h-12 rounded-xl border-slate-200 focus:border-[#0f2847] focus:ring-[#0f2847] tracking-[0.3em] text-center font-bold"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="btn-lift w-full bg-[#0f2847] hover:bg-[#1e3a5f] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Sign in to control room"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <div className="text-center text-xs text-slate-500">
          Secured by Firebase Authentication
        </div>
      </form>
    </AuthLayout>
  );
}
